/**
 * Session Invalid Modal Component
 * 
 * Displays a modal when the user's session is invalid or has been revoked.
 * Handles HTTP 401 responses with "Invalid or inactive token" message.
 */

import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { useAuth } from '../hooks';
import { AuthTokenManager } from '../TokenManager';
import './SessionInvalidModal.css';

/**
 * Session Invalid Modal Component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Session invalid modal component
 */
const SessionInvalidModal = ({
  isOpen = false,
  onClose,
  className = ''
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('🚨 SessionInvalidModal: Modal is OPEN');
    } else {
      console.log('🔍 SessionInvalidModal: Modal is CLOSED');
    }
  }, [isOpen]);

  /**
   * Handle logout - clear tokens and redirect to login
   */
  const handleLogout = useCallback(async () => {
    console.log('🚨 SessionInvalidModal: Logout button clicked');
    
    // Close modal immediately to prevent it from reopening
    if (onClose) {
      onClose();
    }
    
    try {
      // Clear tokens immediately to prevent any API calls from using invalid token
      console.log('🚨 SessionInvalidModal: Clearing tokens immediately');
      AuthTokenManager.clearTokens();
      
      // Also clear user info
      AuthTokenManager.setUserInfo(null);
      
      // Call logout function (this will also clear tokens, but we already did it)
      console.log('🚨 SessionInvalidModal: Calling logout function');
      try {
        await logout();
      } catch (logoutError) {
        // Ignore logout errors - we've already cleared tokens
        console.warn('Logout function error (ignored):', logoutError);
      }
      
      // Force page reload to clear any pending state and navigate to login
      console.log('🚨 SessionInvalidModal: Reloading page to login');
      window.location.href = '/login';
    } catch (error) {
      console.error('SessionInvalidModal logout error:', error);
      // Even if everything fails, navigate to login
      AuthTokenManager.clearTokens();
      AuthTokenManager.setUserInfo(null);
      window.location.href = '/login';
    }
  }, [logout, navigate, onClose]);

  /**
   * Handle click outside modal
   */
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      // Don't allow closing by clicking outside - user must logout
    }
  }, []);

  /**
   * Handle escape key - prevent closing, user must logout
   */
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        // Prevent default - user must logout
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.classList.add('modal-open');
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      className={`session-invalid-modal-overlay ${className}`}
      onClick={handleOverlayClick}
    >
      <div className="session-invalid-modal">
        <div className="session-invalid-modal-content">
          {/* Header */}
          <div className="session-invalid-modal-header">
            <div className="session-invalid-modal-icon">
              <svg className="session-invalid-warning-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="session-invalid-modal-title">
              {t('auth.sessionInvalid.title')}
            </h2>
            <p className="session-invalid-modal-message">
              {t('auth.sessionInvalid.message')}
            </p>
          </div>

          {/* Actions */}
          <div className="session-invalid-modal-actions">
            <button 
              className="session-invalid-modal-btn session-invalid-modal-btn-primary"
              onClick={handleLogout}
            >
              {t('auth.sessionInvalid.logoutButton')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

SessionInvalidModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  className: PropTypes.string,
};

export default SessionInvalidModal;
